/** Tela pública de aceite (SPEC-IDN-011).
 *
 * É o primeiro contato de quem foi convidado com o produto, e ela tem uma
 * responsabilidade que as outras não têm: dizer **para onde** a pessoa está
 * entrando antes de pedir uma senha. Um formulário de senha sem contexto é
 * indistinguível de uma tela de phishing.
 *
 * Link inválido, vencido, revogado e já usado recebem a mesma mensagem —
 * distinguir contaria a quem tenta um link qualquer se ele um dia existiu.
 */

import { useEffect, useState, type SyntheticEvent } from "react";

import { ApiError } from "@/lib/http";
import { Botao } from "@/shared/ui/Botao";
import { Campo } from "@/shared/ui/Campo";
import { FunilDeAmostra, PortalDeAcesso } from "@/shared/ui/PortalDeAcesso";

import { PAPEIS, aceitarConvite, consultarConvite, type ConviteVisivel } from "./api";

const SENHA_MINIMA = 12;

type Estado =
  | { situacao: "carregando" }
  | { situacao: "invalido" }
  | { situacao: "pronto"; convite: ConviteVisivel };

interface Props {
  token: string;
  /** Chamado depois do aceite. A sessão já veio junto — quem monta a aplicação
   * decide o que fazer com ela. */
  aoEntrar: () => void;
}

export function AceitarConvite({ token, aoEntrar }: Props) {
  const [estado, setEstado] = useState<Estado>({ situacao: "carregando" });
  const [senha, setSenha] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    consultarConvite(token)
      .then((convite) => {
        if (vivo) setEstado({ situacao: "pronto", convite });
      })
      .catch(() => {
        if (vivo) setEstado({ situacao: "invalido" });
      });
    return () => {
      vivo = false;
    };
  }, [token]);

  async function enviar(evento: SyntheticEvent) {
    evento.preventDefault();
    if (senha.length < SENHA_MINIMA || enviando) return;

    setEnviando(true);
    setErro(null);
    try {
      await aceitarConvite(token, senha);
      aoEntrar();
    } catch (falha) {
      setErro(
        falha instanceof ApiError ? falha.message : "Não foi possível concluir. Tente de novo.",
      );
      setEnviando(false);
    }
  }

  if (estado.situacao === "carregando") {
    return (
      <Moldura>
        <p role="status" className="font-texto text-body text-texto-suave">
          Verificando o convite…
        </p>
      </Moldura>
    );
  }

  if (estado.situacao === "invalido") {
    return (
      <Moldura>
        <h1 className="font-display text-display-md text-texto">Convite indisponível</h1>
        <p className="font-texto text-body text-texto-suave">
          Este link não vale mais — pode ter expirado, sido cancelado ou já usado. Peça um novo a
          quem convidou você.
        </p>
      </Moldura>
    );
  }

  const papel = PAPEIS.find((item) => item.codigo === estado.convite.role_code);

  return (
    <Moldura>
      <div className="flex flex-col gap-2">
        <p className="font-texto text-micro uppercase text-texto-tenue">Convite</p>
        <h1 className="font-display text-display-md text-texto">
          Entre em {estado.convite.tenant_name}
        </h1>
        <p className="font-texto text-body text-texto-suave">
          {estado.convite.full_name}, você foi convidado como{" "}
          <strong className="text-texto">{papel?.nome ?? estado.convite.role_code}</strong>. Escolha
          uma senha para começar.
        </p>
        <p className="font-dados text-caption text-texto-tenue">{estado.convite.email}</p>
      </div>

      <form onSubmit={(evento) => void enviar(evento)} className="flex flex-col gap-4">
        <Campo
          rotulo="Senha"
          type="password"
          value={senha}
          onChange={(evento) => {
            setSenha(evento.target.value);
          }}
          autoComplete="new-password"
          ajuda={`Ao menos ${SENHA_MINIMA} caracteres.`}
          required
          autoFocus
        />

        <Botao
          type="submit"
          larguraTotal
          carregando={enviando}
          disabled={senha.length < SENHA_MINIMA}
        >
          Entrar
        </Botao>

        {erro !== null && (
          <p role="alert" className="font-texto text-caption text-carmim">
            {erro}
          </p>
        )}
      </form>
    </Moldura>
  );
}

function Moldura({ children }: { children: React.ReactNode }) {
  return <PortalDeAcesso painel={<OTimeJaEsta />}>{children}</PortalDeAcesso>;
}

/** O painel de quem foi trazido por alguém.
 *
 * Esta tela carrega o peso que as outras duas não têm: **um formulário de senha
 * sem contexto é indistinguível de phishing**. O nome de quem convida e o da
 * empresa já aparecem no formulário, vindos da API; o painel completa dizendo
 * para dentro de quê a pessoa está entrando — um quadro que já existe e já tem
 * trabalho em andamento.
 *
 * O mesmo funil do cadastro, e de propósito: quem cria a conta e quem é
 * convidado chegam ao mesmo lugar, e a tela seguinte é a mesma para os dois.
 */
function OTimeJaEsta() {
  return (
    <div className="flex max-w-[440px] flex-col gap-6">
      <p className="font-display text-display-lg leading-tight text-acao-contraste">
        O funil do time já está rodando.
      </p>
      <p className="font-texto text-body-lg text-acao-contraste/85">
        Escolha uma senha e você entra direto no quadro, com os negócios que já estão lá.
      </p>

      <FunilDeAmostra />

      <p className="font-texto text-caption text-acao-contraste/75">
        Todo mundo da empresa acompanha o mesmo funil — é assim que o time sabe em que pé está cada
        conversa.
      </p>
    </div>
  );
}
